// #region IMPORT
import type { AnimationClip } from 'three';
import { Bone, Quaternion, Skeleton, Vector3 } from 'three';

// @ts-expect-error ts(7016) : Could not find a declaration file for module
import { Armature, Pose } from './ossos.min.js';

// #endregion

export function skeletonToArmature(skel: Skeleton) {
  const arm = new Armature();

  let b;
  for (const sb of skel.bones) {
    b = arm.addBone({
      name: sb.name,
      parent: sb.parent instanceof Bone ? sb.parent.name : null,
    });

    b.local.rot.copy(sb.quaternion.toArray());
    b.local.pos.copy(sb.position.toArray());
    b.local.scl.copy(sb.scale.toArray());
  }

  arm.bind(0.1);
  return arm;
}

export function applyPoseToSkeleton(
  pose: Pose,
  skel: Skeleton,
  posList?: Array<string>,
) {
  let sb;
  for (const [i, pb] of pose.bones.entries()) {
    sb = skel.bones[i];
    sb.quaternion.fromArray(pb.local.rot);

    if (posList && posList.includes(sb.name)) {
      sb.position.fromArray(pb.local.pos);
    }
  }
}

export function armaturePoseFromClip(
  pose: any,
  clip: AnimationClip,
  frame: number = 0,
  posAllowed?: Array<string>,
): void {
  const vIdx: number = frame * 3; // Starting index to parse frame vector3 data
  const qIdx: number = frame * 4; // Starting index to parse frame quaternion data
  const bind = pose.clone().reset(); // Get initial bindpose

  let idx: number;
  let bName: string; // Parsed Bone Name
  let propType: string; // Parsed property [ position, quaternion ]
  let bone: any; // Ossos pose bone
  let bbone: any; // Ossos pose that has been cloned & reset to its bind values.

  for (const t of clip.tracks) {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Parse & prep
    idx = t.name.lastIndexOf('.');
    bName = t.name.substring(0, idx);
    propType = t.name.substring(idx + 1);
    bone = pose.getBone(bName);

    if (!bone) {
      console.log(
        'armaturePoseFromClip - Bone name not found in skeleton: ',
        bName,
      );
      continue;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Apply data
    switch (propType) {
      case 'quaternion':
        bone.local.rot[0] = t.values[qIdx + 0];
        bone.local.rot[1] = t.values[qIdx + 1];
        bone.local.rot[2] = t.values[qIdx + 2];
        bone.local.rot[3] = t.values[qIdx + 3];
        break;
      case 'position':
        if (!posAllowed || posAllowed?.includes(bName)) {
          // BVHLoader modifies the animation position data by adding
          // the bone offset ( local space position ) to it. This creates
          // incorrect positioning. To fix the issue, remove the offset
          // value from the animation data.
          bbone = bind.getBone(bName);

          bone.local.pos[0] = t.values[vIdx + 0] - bbone.local.pos[0];
          bone.local.pos[1] = t.values[vIdx + 1] - bbone.local.pos[1];
          bone.local.pos[2] = t.values[vIdx + 2] - bbone.local.pos[2];
        }
        break;
    }
  }

  pose.updateWorld();
}

export function skeletonPoseFromClip(
  skel: Skeleton,
  clip: AnimationClip,
  frame: number = 0,
  posAllowed?: Array<string>,
): void {
  const vIdx: number = frame * 3; // Starting index to parse frame vector3 data
  const qIdx: number = frame * 4; // Starting index to parse frame quaternion data

  let idx: number;
  let bName: string; // Parsed Bone Name
  let propType: string; // Parsed property [ position, quaternion ]
  let bone: Bone | undefined; // 3JS Skeleton Bone

  for (const t of clip.tracks) {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Parse & prep
    idx = t.name.lastIndexOf('.');
    bName = t.name.substring(0, idx);
    propType = t.name.substring(idx + 1);
    bone = skel.getBoneByName(bName);

    if (!bone) {
      console.log(
        'skeletonPoseFromClip - Bone name not found in skeleton: ',
        bName,
      );
      continue;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Apply data
    switch (propType) {
      case 'quaternion':
        bone.quaternion.x = t.values[qIdx + 0];
        bone.quaternion.y = t.values[qIdx + 1];
        bone.quaternion.z = t.values[qIdx + 2];
        bone.quaternion.w = t.values[qIdx + 3];

        break;
      case 'position':
        if (!posAllowed || posAllowed?.includes(bName)) {
          bone.position.x = t.values[vIdx + 0];
          bone.position.y = t.values[vIdx + 1];
          bone.position.z = t.values[vIdx + 2];
        }
        break;
    }
  }
}

export function cloneSkeleton(skel: Skeleton) {
  const bmap: Record<string, Bone> = {};
  const clone = skel.clone();

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // TODO : https://github.com/mrdoob/three.js/issues/31548
  // There is a "bug" in skeleton clone where bones are not properly cloned
  // but just a shallow copy of the bones to the new skeleton object. This is
  // a problem where skeleton can not be used to store multiple sets of poses.
  // By modifing one bone, the change will be reflected on all cloned skeletons.
  // To avoid this issue, need to take the extra step of cloning the bones.
  // At the moment, the issue has been tagged as a "suggestion" instead of a bug.
  // If by chance the bug gets fixed, there will be no need for this for loop.
  for (const [i, b] of skel.bones.entries()) {
    clone.bones[i] = b.clone();
    bmap[b.name] = clone.bones[i];
  }

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Fix Parent & Children
  for (const [i, b] of clone.bones.entries()) {
    // Link cloned parent bone
    // @ts-expect-error ts(2339) : Need to test if isBone exists
    if (skel.bones[i].parent && skel.bones[i].parent.isBone) {
      b.parent = bmap[skel.bones[i].parent.name];
    }

    // Link to cloned children
    for (const [ci, cb] of b.children.entries()) {
      b.children[ci] = bmap[cb.name];
    }
  }

  return clone;
}
